import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";

export function LoginPage() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/app" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full grid lg:grid-cols-[1.1fr_0.9fr]">
      <section className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden bg-navy">
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(ellipse at 20% 20%, rgba(0,139,139,0.45), transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(197,160,89,0.25), transparent 45%), linear-gradient(160deg, #001a38, #002855 55%, #003d66)",
          }}
        />
        <div className="relative z-10">
          <img
            src="/govsuite-logo-on-dark.png"
            alt="GovSuite"
            className="h-12 w-auto max-w-[280px] object-contain object-left"
          />
          <div className="mt-2 text-sm text-white/70">Demo sandbox</div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl leading-tight mb-4">
            Explore the product with realistic sample data
          </h1>
          <p className="text-white/80 text-lg">
            Sample Tenant space — switch roles anytime to see each workbench.
          </p>
        </div>
        <div className="relative z-10" aria-hidden="true" />
      </section>

      <section className="flex items-center justify-center p-8 bg-mist">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md bg-white border border-black/5 shadow-[0_20px_60px_rgba(0,40,85,0.08)] p-8"
        >
          <div className="lg:hidden mb-6">
            <img
              src="/govsuite-logo.png"
              alt="GovSuite"
              className="h-10 w-auto max-w-[220px] object-contain object-left"
            />
          </div>
          <h2 className="text-2xl font-semibold text-navy mb-1">Sign in</h2>
          <p className="text-slate text-sm mb-6">
            Use the temporary password from your demo invite email.
          </p>
          <label className="block text-sm font-medium text-navy mb-1">Email</label>
          <input
            className="w-full mb-4 px-3 py-2.5 border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label className="block text-sm font-medium text-navy mb-1">Password</label>
          <input
            className="w-full mb-4 px-3 py-2.5 border border-black/10 bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2">{error}</div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full py-3 bg-navy text-white font-medium hover:bg-navy-deep disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Enter demo"}
          </button>
        </form>
      </section>
    </div>
  );
}
